import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { AttendanceDetail } from "../components/AttendanceDetail";
import { Avatar, Badge, Button, EmptyState, Skeleton } from "../components/ui";

/**
 * One employee's attendance, reached by clicking a name in the register.
 * It is the same component the Employee Profile's Attendance tab renders, so
 * the two can never drift apart.
 */
export function AttendanceDetailPage() {
  const { employeeId } = useParams();
  const { user } = useAuth();
  const timezone = user?.account?.timezone;

  const employee = useQuery({
    queryKey: ["employee", employeeId],
    queryFn: ({ signal }) => api.employee(employeeId!, signal),
  });

  if (employee.isPending) return <Skeleton className="h-24 w-full" />;
  if (employee.isError) {
    return (
      <EmptyState
        title="Could not load employee"
        body={employee.error instanceof ApiError ? employee.error.message : "Please try again."}
      />
    );
  }

  const record = employee.data;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Avatar name={record.display_name} src={record.photo_data || record.photo_url} size={56} />
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">{record.display_name}</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {record.employee_code} · {record.designation || "Employee"}
            </p>
            <div className="mt-1.5">
              <Badge tone={record.status === "active" ? "emerald" : "slate"}>
                {record.status?.replace(/_/g, " ")}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/people/employees/${record.id}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
          >
            Open profile
          </Link>
          <Link to="/people/attendance">
            <Button variant="ghost">
              <ArrowLeft size={15} /> Attendance
            </Button>
          </Link>
        </div>
      </div>

      <AttendanceDetail employeeId={record.id} timezone={timezone} />
    </>
  );
}
